import { BffBrandType } from '@/types/generated';

import { cacheFn } from '@utils/cacheFn';
import { getGraphHeaders } from '@utils/getGraphHeaders';
import { logWarning, logError } from '@utils/miscUtils';

type AdditionalParams = {
	// SonarQube rule: Reduce the number of parameters
	radiusMiles?: number;
	maxItems?: number;
	fetchAllIfNoResults?: boolean;
};

export const resolveAdditionalParams = (additionalParams?: AdditionalParams) => ({
	fetchAllIfNoResults: !!additionalParams?.fetchAllIfNoResults,
	radiusMiles: additionalParams?.radiusMiles ?? 50,
	maxItems: additionalParams?.maxItems ?? 1000,
});

export const getBffNearByFacilitiesByLocation = cacheFn(
	async (
		brand: BffBrandType,
		latitude: number,
		longitude: number,
		url: string,
		appName: string,
		appVersion: string,
		additionalParams?: AdditionalParams
	) => {
		try {
			if (!brand) {
				return null;
			}

			if (!latitude || !longitude) {
				throw new Error(`Invalid coordinates of latitude: ${latitude} and longitude: ${longitude}`);
			}
			const { getGraphQLClient } = await import(
				/* webpackChunkName:"getGraphQLClient" */ '@src/lib/graphql/getGraphQLClient'
			);
			return await getGraphQLClient(url).bffNearByFacilitiesByLocation(
				{
					brand,
					latitude,
					longitude,
					...resolveAdditionalParams(additionalParams),
				},
				getGraphHeaders(appName, appVersion)
			);
		} catch (err) {
			logWarning(`Could not find any near by facilities data for: ${brand}`);
			logError(err instanceof Error ? err.message : String(err));
			return undefined;
		}
	},
	{
		// Key on the resolved request so callers that omit options and callers that pass the defaults share one request
		keyGenerator: ([brand, latitude, longitude, url, appName, appVersion, additionalParams]) =>
			JSON.stringify([
				brand,
				latitude,
				longitude,
				url,
				appName,
				appVersion,
				resolveAdditionalParams(additionalParams),
			]),
	}
);
